/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  COSTO_ADQUISICION_REF,
  ESCENARIOS_CAPTACION_TELCOS,
  IPSOS_HOGAR,
  IPSOS_MOVIL,
} from "../../data/ipsos2025/telcos";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaTelcos.module.scss";

export const Telcos = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [operadorSeleccionado, setOperadorSeleccionado] = useState("ENTEL");
  const [proveedorHogarSeleccionado, setProveedorHogarSeleccionado] = useState("Movistar");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [vistaActiva, setVistaActiva] = useState("movil"); // 'movil' o 'hogar'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  // =====================================================
  // CÁLCULOS TELEFONÍA MÓVIL
  // =====================================================
  const resultadosMovil = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Clientes B2C (personal) y B2B (corporativo)
    const clientesB2B = Math.round(totalEjecutivos * IPSOS_MOVIL.telefonoCorporativo.valor);
    const clientesB2C = totalEjecutivos; // Todos tienen teléfono personal
    const clientesTotales = clientesB2C + clientesB2B;

    // Desglose por operador
    const operadores = {};
    let mercadoMensualTotal = 0;

    Object.entries(IPSOS_MOVIL.operadores).forEach(([nombre, data]) => {
      const clientesB2COp = Math.round(clientesB2C * data.share.valor);
      const clientesB2BOp = Math.round(clientesB2B * data.share.valor);
      const totalOp = clientesB2COp + clientesB2BOp;
      const mercadoMensual = totalOp * data.precioMes.valor;

      operadores[nombre] = {
        shareDato: data.share,
        share: data.share.valor * 100,
        clientesB2C: clientesB2COp,
        clientesB2B: clientesB2BOp,
        clientesTotales: totalOp,
        precioMes: data.precioMes.valor,
        mercadoMensual,
        mercadoAnual: mercadoMensual * 12,
      };

      mercadoMensualTotal += mercadoMensual;
    });

    // Oportunidad de churn
    const clientesCambiarian = Math.round(clientesB2C * IPSOS_MOVIL.cambiariaPlan.valor);

    return {
      totalEjecutivos,
      clientesB2C,
      clientesB2B,
      clientesTotales,
      operadores,
      mercadoMensualTotal,
      mercadoAnualTotal: mercadoMensualTotal * 12,
      clientesCambiarian,
      pctCambiarian: IPSOS_MOVIL.cambiariaPlan.valor * 100,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS INTERNET HOGAR
  // =====================================================
  const resultadosHogar = useMemo(() => {
    if (!totalEjecutivos) return null;

    const hogaresInternetFijo = Math.round(totalEjecutivos * IPSOS_HOGAR.tieneInternetFijo.valor);
    // Base del reparto por operador: hogares con TV pagada (lámina 98), no los de internet fijo
    const hogaresTvPagada = Math.round(totalEjecutivos * IPSOS_HOGAR.tieneTvPagada.valor);

    // Desglose por operador de TV pagada
    const proveedores = {};
    let mercadoMensualTotal = 0;

    Object.entries(IPSOS_HOGAR.proveedores).forEach(([nombre, dato]) => {
      const hogares = Math.round(hogaresTvPagada * dato.valor);
      const mercadoMensual = hogares * IPSOS_HOGAR.precioMensualPromedio.valor;

      proveedores[nombre] = {
        dato,
        share: dato.valor * 100,
        hogares,
        precioMes: IPSOS_HOGAR.precioMensualPromedio.valor,
        mercadoMensual,
        mercadoAnual: mercadoMensual * 12,
      };

      mercadoMensualTotal += mercadoMensual;
    });

    return {
      totalEjecutivos,
      hogaresInternetFijo,
      pctInternetFijo: IPSOS_HOGAR.tieneInternetFijo.valor * 100,
      hogaresTvPagada,
      pctTvPagadaDato: IPSOS_HOGAR.tieneTvPagada,
      pctTvPagada: IPSOS_HOGAR.tieneTvPagada.valor * 100,
      proveedores,
      mercadoMensualTotal,
      mercadoAnualTotal: mercadoMensualTotal * 12,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN MÓVIL
  // =====================================================
  const calcularOportunidadMovil = useCallback(
    (operador, pctCaptacion) => {
      if (!resultadosMovil) return null;

      const datosOp = resultadosMovil.operadores[operador];
      if (!datosOp) return null;

      // Del 29.9% que cambiaría, captar un % específico
      const clientesPotenciales = resultadosMovil.clientesCambiarian;
      const clientesCaptados = Math.round(clientesPotenciales * (pctCaptacion / 100));
      const mercadoGanadoMensual = clientesCaptados * datosOp.precioMes;

      return {
        clientesPotenciales,
        clientesCaptados,
        mercadoGanadoMensual,
        mercadoGanadoAnual: mercadoGanadoMensual * 12,
        costoAdquisicionRef: COSTO_ADQUISICION_REF.valor, // Pauta 3 meses referencia
      };
    },
    [resultadosMovil]
  );

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN HOGAR
  // =====================================================
  const calcularOportunidadHogar = useCallback(
    (proveedor, pctCaptacion) => {
      if (!resultadosHogar) return null;

      const datosP = resultadosHogar.proveedores[proveedor];
      if (!datosP) return null;

      // Captar del mercado total de hogares con TV pagada
      const clientesCaptados = Math.round(resultadosHogar.hogaresTvPagada * (pctCaptacion / 100));
      const mercadoGanadoMensual = clientesCaptados * IPSOS_HOGAR.precioMensualPromedio.valor;

      return {
        hogaresTotales: resultadosHogar.hogaresTvPagada,
        clientesCaptados,
        mercadoGanadoMensual,
        mercadoGanadoAnual: mercadoGanadoMensual * 12,
      };
    },
    [resultadosHogar]
  );

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.telcosPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>TELECOMUNICACIONES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.telcosContent}>
        {/* Tabs para cambiar entre Móvil y Hogar */}
        <div className={styles.tabsContainer}>
          <button
            className={cx(styles.tab, vistaActiva === "movil" && styles.tabActivo)}
            onClick={() => setVistaActiva("movil")}
          >
            Telefonía Móvil
          </button>
          <button
            className={cx(styles.tab, vistaActiva === "hogar" && styles.tabActivo)}
            onClick={() => setVistaActiva("hogar")}
          >
            Internet Hogar
          </button>
        </div>

        {/* =====================================================
            VISTA TELEFONÍA MÓVIL
            ===================================================== */}
        {vistaActiva === "movil" && resultadosMovil && (
          <>
            {/* Tabla resumen */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thSmall)}>B2C</th>
                    <th className={cx("text-center", styles.thSmall)}>B2B</th>
                    <th className={cx("text-center", styles.thSmall)}>Churn</th>
                    <th className={cx("text-center", styles.thMercado)}>
                      <div className={styles.headerConsumos}>
                        <span>Mercado</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <BadgeDato dato={IPSOS_MOVIL.tipoPlan.postpago}>
                          {(IPSOS_MOVIL.tipoPlan.postpago.valor * 100).toFixed(0)}%
                        </BadgeDato>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesB2C)}</div>
                        <div className={styles.subtexto}>líneas personales</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <BadgeDato dato={IPSOS_MOVIL.telefonoCorporativo}>
                          {(IPSOS_MOVIL.telefonoCorporativo.valor * 100).toFixed(1)}%
                        </BadgeDato>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesB2B)}</div>
                        <div className={styles.subtexto}>líneas corporativas</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaOportunidad)}>
                      <div className={styles.celdaContenido}>
                        <BadgeDato dato={IPSOS_MOVIL.cambiariaPlan} className={styles.badgeOportunidad}>
                          {(IPSOS_MOVIL.cambiariaPlan.valor * 100).toFixed(1)}%
                        </BadgeDato>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesCambiarian)}</div>
                        <div className={styles.subtexto}>cambiarían de operador</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaTotal)}>
                      <div className={styles.valorDestacado}>
                        {formatLocal(
                          convertCLP(verAnual ? resultadosMovil.mercadoAnualTotal : resultadosMovil.mercadoMensualTotal)
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Market Share por Operador */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Operador</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosMovil.operadores).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <BadgeDato dato={data.shareDato}>{data.share.toFixed(0)}%</BadgeDato>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientesTotales)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 96, base 400):</strong> 100% tiene plan postpago. 36,2% tiene teléfono
                corporativo (B2B). 29,9% cambiaría de operador por oferta tentadora.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> precio mensual del plan por operador.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA INTERNET HOGAR
            ===================================================== */}
        {vistaActiva === "hogar" && resultadosHogar && (
          <>
            {/* Tabla resumen */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thSmall)}>Tienen TV pagada</th>
                    <th className={cx("text-center", styles.thSmall)}>Precio prom.</th>
                    <th className={cx("text-center", styles.thMercado)}>
                      <div className={styles.headerConsumos}>
                        <span>Mercado</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <BadgeDato dato={resultadosHogar.pctTvPagadaDato}>
                          {Math.round(resultadosHogar.pctTvPagada)}%
                        </BadgeDato>
                        <div className={styles.valorGrande}>{formatNumber(resultadosHogar.hogaresTvPagada)}</div>
                        <div className={styles.subtexto}>hogares con TV pagada</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle")}>
                      <div className={styles.celdaContenido}>
                        <div className={styles.valorGrande}>
                          {formatLocal(convertCLP(IPSOS_HOGAR.precioMensualPromedio.valor))}
                        </div>
                        <div className={styles.subtexto}>mensual por hogar</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaTotal)}>
                      <div className={styles.valorDestacado}>
                        {formatLocal(
                          convertCLP(verAnual ? resultadosHogar.mercadoAnualTotal : resultadosHogar.mercadoMensualTotal)
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* TV pagada: operador */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>TV pagada: operador</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosHogar.proveedores).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <BadgeDato dato={data.dato}>{data.share.toFixed(0)}%</BadgeDato>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Hogares</span>
                        <span className={styles.metricaValor}>{formatNumber(data.hogares)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 98, base 400/206):</strong> 90,2% tiene internet fijo; 51,4% tiene TV pagada;
                operador de TV pagada según Ipsos, base 206.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> precio promedio mensual por hogar (
                {formatLocal(convertCLP(IPSOS_HOGAR.precioMensualPromedio.valor))}).
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosMovil || resultadosHogar) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - {vistaActiva === "movil" ? "Telefonía Móvil" : "Internet Hogar"}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {vistaActiva === "movil" && resultadosMovil && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Operador objetivo:</label>
                      <select
                        value={operadorSeleccionado}
                        onChange={(e) => setOperadorSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MOVIL.operadores).map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación del churn (supuesto):</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION_TELCOS.map((p) => (
                          <option key={p.valor} value={p.valor}>
                            {p.valor}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadMovil(operadorSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>
                            Clientes que cambiarían ({(IPSOS_MOVIL.cambiariaPlan.valor * 100).toFixed(1)}%):
                          </span>
                          <span className={styles.resultadoValor}>{formatNumber(oportunidad.clientesPotenciales)}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            +{formatNumber(oportunidad.clientesCaptados)} clientes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/mes:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoMensual))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/año (contrato 12m):</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {vistaActiva === "hogar" && resultadosHogar && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Operador objetivo:</label>
                      <select
                        value={proveedorHogarSeleccionado}
                        onChange={(e) => setProveedorHogarSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_HOGAR.proveedores).map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado (supuesto):</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION_TELCOS.map((p) => (
                          <option key={p.valor} value={p.valor}>
                            {p.valor}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadHogar(proveedorHogarSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Hogares con TV pagada (51%):</span>
                          <span className={styles.resultadoValor}>{formatNumber(oportunidad.hogaresTotales)}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            +{formatNumber(oportunidad.clientesCaptados)} hogares
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/mes:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoMensual))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/año:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Telcos;
